// @flow
import {
  SectionsProgress,
  SectionCompletionStatus,
  SectionStep,
  SectionSteps,
} from './types';
import SECTION_NAMES, {
  PATH_TO_SECTION_MAP,
  SECTION_TO_PATH_MAP,
  SectionNames,
  SectionParentNames,
} from 'constants/sectionNames';
import getProgressFromProgressArray from 'utils/getProgressFromProgressArray';
import { completionPercentage } from 'utils/affordaPortal';

export const getSectionNameFromPathname = ({ pathname, baseURL }) => {
  // Remove the base URL from the pathname to get for example:
  // assets or wages-and-income
  const sectionPathRoot = pathname.replace(`${baseURL}/`, '').split('/')[0];
  const sectionName = PATH_TO_SECTION_MAP[sectionPathRoot];
  return sectionName;
};

export const getRequiredSteps = ({
  currentSectionProgressSteps = [],
  isSectionsProgressLoggingEnabled,
}: {
  currentSectionProgressSteps: SectionSteps,
  isSectionsProgressLoggingEnabled: boolean,
}): {
  completed: SectionSteps,
  required: SectionSteps,
} => {
  const requiredHouseholdSteps = currentSectionProgressSteps.filter(
    (step) => step.required,
  );

  if (isSectionsProgressLoggingEnabled) {
    console.group('REQUIRED STEPS UPDATE');
    requiredHouseholdSteps.forEach((step) => {
      console.log('REQUIRED STEP:', step.complete, step.name);
    });
    console.groupEnd();
    console.log('');
  }

  return {
    completed: requiredHouseholdSteps?.filter((step) => step.complete),
    required: requiredHouseholdSteps,
  };
};

export const getCurrentStep = ({ requiredSteps }) => {
  return requiredSteps.find((step) => !step.complete);
};

export const getNextStep = ({
  requiredSteps,
}: {
  requiredSteps: SectionSteps,
}): SectionStep => {
  // Find the current step
  const currentStep = getCurrentStep({ requiredSteps });
  if (!currentStep) return null;
  // Locate the index of the current step
  const currentStepIndex = requiredSteps.findIndex(
    (step) => step.path === currentStep.path,
  );
  // Start looking for the next required step from the current step index + 1
  for (let i = currentStepIndex + 1; i < requiredSteps.length; i++) {
    if (requiredSteps[i].required && !requiredSteps[i].complete) {
      return requiredSteps[i];
    }
  }
  // Return null if no next required step is found
  return null;
};

export const getPreviousStep = ({
  requiredSteps,
}: {
  requiredSteps: SectionSteps,
}): SectionStep => {
  // Find the current step
  const currentStep = getCurrentStep({ requiredSteps });

  if (!currentStep) return null;

  // Locate the index of the current step
  const currentStepIndex = requiredSteps.findIndex(
    (step) => step.path === currentStep.path,
  );

  // Calculate the index of the previous step
  const previousStepIndex = currentStepIndex - 1;

  // Return the previous step if valid, otherwise null
  return previousStepIndex >= 0 ? requiredSteps[previousStepIndex] : null;
};

export const getStepFromPathname = ({ requiredSteps, pathname }) => {
  return requiredSteps.find((step) => pathname.endsWith(step.path));
};

const getApplicationSectionNames = ({
  isPrimaryProspect,
  isUnInvitedSecondaryUser,
}) => {
  if (isUnInvitedSecondaryUser) return [];
  else if (isPrimaryProspect) {
    return [
      SECTION_NAMES.HOUSEHOLD,
      SECTION_NAMES.WAGES_AND_INCOME,
      SECTION_NAMES.ASSETS,
    ];
  } else if (!isPrimaryProspect) {
    return [SECTION_NAMES.WAGES_AND_INCOME, SECTION_NAMES.ASSETS];
  }
};

const getCreditAndCriminalSectionNames = ({
  isPrimaryProspect,
  isUnInvitedSecondaryUser,
}) => {
  if (isUnInvitedSecondaryUser) {
    return [
      SECTION_NAMES.IDENTIFYING_DETAILS,
      SECTION_NAMES.CREDIT_CRIMINAL_CHECK,
    ];
  } else if (isPrimaryProspect) {
    return [
      SECTION_NAMES.PAYMENTS,
      SECTION_NAMES.IDENTIFYING_DETAILS,
      SECTION_NAMES.CREDIT_CRIMINAL_CHECK,
    ];
  } else if (!isPrimaryProspect) {
    return [
      SECTION_NAMES.IDENTIFYING_DETAILS,
      SECTION_NAMES.CREDIT_CRIMINAL_CHECK,
    ];
  }
};

const getDocumentationSectionNames = ({
  isPrimaryProspect,
  isUnInvitedSecondaryUser,
}) => {
  if (isUnInvitedSecondaryUser) {
    return [
      SECTION_NAMES.SELF_IDENTIFICATION,
      SECTION_NAMES.APPLICATION_SUBMISSION,
    ];
  } else {
    return [
      SECTION_NAMES.SELF_IDENTIFICATION,
      SECTION_NAMES.DOCUMENTS_AND_UPLOADS,
      SECTION_NAMES.APPLICATION_SUBMISSION,
    ];
  }
};

export const getSectionParentName = (
  sectionName: SectionNames,
  isPrimaryProspect: boolean,
  isUnInvitedSecondaryUser: boolean,
): SectionParentNames => {
  const isApplicationSection = getApplicationSectionNames({
    isPrimaryProspect,
    isUnInvitedSecondaryUser,
  }).includes(sectionName);

  const isCreditAndCriminalSection = getCreditAndCriminalSectionNames({
    isPrimaryProspect,
    isUnInvitedSecondaryUser,
  }).includes(sectionName);

  const isDocumentationSection = getDocumentationSectionNames({
    isPrimaryProspect,
    isUnInvitedSecondaryUser,
  }).includes(sectionName);

  switch (true) {
    case isApplicationSection:
      return SECTION_NAMES.APPLICATION;
    case isCreditAndCriminalSection:
      return SECTION_NAMES.CREDIT_AND_CRIMINAL;
    case isDocumentationSection:
      return SECTION_NAMES.DOCUMENTATION;
    default:
      return null;
  }
};

const getSectionParentPath = (
  sectionName: SectionNames,
  isPrimaryProspect: boolean,
  isUnInvitedSecondaryUser: boolean,
) => {
  const sectionParentName = getSectionParentName(
    sectionName,
    isPrimaryProspect,
    isUnInvitedSecondaryUser,
  );

  return SECTION_TO_PATH_MAP[sectionParentName] ?? null;
};

export const getSectionNamesFromParent = (
  parentSectionName: SectionParentNames,
  isPrimaryProspect: boolean,
  isUnInvitedSecondaryUser: boolean,
): Array<SectionNames> => {
  switch (parentSectionName) {
    case SECTION_NAMES.APPLICATION:
      return getApplicationSectionNames({
        isPrimaryProspect,
        isUnInvitedSecondaryUser,
      });
    case SECTION_NAMES.CREDIT_AND_CRIMINAL:
      return getCreditAndCriminalSectionNames({
        isPrimaryProspect,
        isUnInvitedSecondaryUser,
      });
    case SECTION_NAMES.DOCUMENTATION:
      return getDocumentationSectionNames({
        isPrimaryProspect,
        isUnInvitedSecondaryUser,
      });
    default:
      return null;
  }
};

export const getStepsToCleanup = (steps: SectionSteps): SectionSteps => {
  const { required: requiredSteps } = getRequiredSteps({
    currentSectionProgressSteps: steps,
  });
  const currentStep = getCurrentStep({ requiredSteps });
  if (!currentStep) return [];

  return steps.filter((step) => step.required !== true);
};

export const getStepURLs = ({
  steps,
  sectionName,
  baseURL,
  isPrimaryUser,
  isUnInvitedSecondaryUser,
}) => {
  const { required: requiredSteps } = getRequiredSteps({
    currentSectionProgressSteps: steps,
  });
  const currentStep = getCurrentStep({
    requiredSteps,
  });
  const nextStep = getNextStep({ requiredSteps });
  const previousStep = getPreviousStep({
    requiredSteps,
  });

  const currentPath = currentStep?.path ?? null;
  const nextPath = nextStep?.path ?? null;
  let previousPath = previousStep?.path ?? null;

  if (!previousPath) {
    const parentSectionPath = getSectionParentPath(
      sectionName,
      isPrimaryUser,
      isUnInvitedSecondaryUser,
    );

    if (parentSectionPath) {
      previousPath = parentSectionPath;
    }
  }

  return {
    previousURL: previousPath ? `${baseURL}/${previousPath}` : null,
    nextURL: nextPath ? `${baseURL}/${nextPath}` : null,
    currentURL: currentPath ? `${baseURL}/${currentPath}` : null,
  };
};

export const determineSectionsProgressUpdate = ({
  currentSectionProgress,
  isPrimaryProspect,
  isUnInvitedSecondaryUser,
  sectionName,
  sectionsProgress,
  update,
}: {
  currentSectionProgress: SectionCompletionStatus,
  isPrimaryProspect: boolean,
  isUnInvitedSecondaryUser: boolean,
  sectionName: SectionNames,
  sectionsProgress: SectionsProgress,
  update: SectionCompletionStatus,
}) => {
  let updatedCurrentSectionProgress = {
    ...currentSectionProgress,
    ...update,
    isComplete: Boolean(update.progress === 100),
  };

  const updatedSectionProgress = {
    ...sectionsProgress,
    [sectionName]: updatedCurrentSectionProgress,
  };

  const childSectionNames = getSectionNamesFromParent(
    currentSectionProgress.parentName,
    isPrimaryProspect,
    isUnInvitedSecondaryUser,
  );

  const childSectionsProgressArray = childSectionNames.map(
    (childSectionName) =>
      updatedSectionProgress[childSectionName]?.progress ?? 0,
  );
  const parentProgress = getProgressFromProgressArray(
    childSectionsProgressArray,
  );

  updatedSectionProgress[currentSectionProgress.parentName].progress =
    parentProgress;
  updatedSectionProgress[currentSectionProgress.parentName].isComplete =
    Boolean(parentProgress === 100);

  return updatedSectionProgress;
};

export const hasInitialStepsChanged = (
  initialSteps: SectionSteps,
  currentSteps: SectionSteps,
): boolean => {
  // Check if the number of steps is different
  if (initialSteps.length !== currentSteps.length) {
    return true;
  }

  // Iterate through each step and compare the required property
  for (let i = 0; i < initialSteps.length; i++) {
    if (initialSteps[i].complete !== currentSteps[i].complete) {
      return true;
    }
    if (initialSteps[i].required !== currentSteps[i].required) {
      return true;
    }
  }

  // If no differences found, return false
  return false;
};

export const mergeInitialAndCurrentSteps = (
  initialSteps: SectionSteps,
  currentSteps: SectionSteps,
): SectionSteps => {
  const steps = [];

  currentSteps.forEach((step) => {
    const initialStep = initialSteps.find(
      (initialStep) => initialStep.path === step.path,
    );

    if (initialStep) {
      steps.push({ ...step, required: initialStep.required });
    }
  });

  return steps;
};

export const getUpdatedSteps = ({
  currentSectionProgress,
  effectMode,
  initialSteps,
  isPrimaryProspect,
  isSectionsProgressLoggingEnabled,
  isUnInvitedSecondaryUser,
  requiredSteps,
  sectionName,
  sectionsProgress,
}): {
  updatedSteps: SectionSteps,
  updatedSectionsProgress: SectionsProgress,
} => {
  if (isSectionsProgressLoggingEnabled) {
    console.group(`INITIALIZE SECTION STEPS: ${sectionName}`);
  }

  const values = {
    steps: currentSectionProgress.steps,
    currentSectionProgress,
    sectionsProgress,
    requiredSteps,
  };

  const hasExistingSectionProgressSteps = Boolean(values.steps.length);

  const shouldUpdateExistingSteps = hasExistingSectionProgressSteps
    ? hasInitialStepsChanged(initialSteps, values.steps)
    : false;

  if (!shouldUpdateExistingSteps && hasExistingSectionProgressSteps) {
    if (isSectionsProgressLoggingEnabled)
      console.log('Has existing steps and no changes', {
        initialSteps,
        currentSteps: values.steps,
      });

    if (!requiredSteps.required.length) {
      if (isSectionsProgressLoggingEnabled) console.log('Set existing steps');

      return {
        updatedSteps: values.steps,
        updatedSectionsProgress: null,
      };
    }

    if (isSectionsProgressLoggingEnabled) console.groupEnd();

    return !effectMode
      ? {
          updatedSteps: values.steps,
          updatedSectionsProgress: null,
        }
      : {
          updatedSteps: null,
          updatedSectionsProgress: null,
        };
  }

  let sectionsProgressUpdate: SectionsProgress = {};

  if (shouldUpdateExistingSteps) {
    // update existing completion status
    sectionsProgressUpdate = {
      ...values.sectionsProgress,
      [sectionName]: {
        ...values.currentSectionProgress,
        steps: mergeInitialAndCurrentSteps(initialSteps, values.steps),
      },
    };
    if (isSectionsProgressLoggingEnabled) {
      console.log('Update existing steps');
    }
  } else {
    // add new completion status
    if (isSectionsProgressLoggingEnabled) {
      console.log('Add new section progress with initial steps');
    }
    sectionsProgressUpdate = {
      ...sectionsProgress,
      [sectionName]: {
        name: sectionName,
        parentName: getSectionParentName(
          sectionName,
          isPrimaryProspect,
          isUnInvitedSecondaryUser,
        ),
        progress: 1,
        isComplete: false,
        steps: initialSteps,
      },
    };

    // add parent if it doesn't exist
    const sectionParentName = getSectionParentName(
      sectionName,
      isPrimaryProspect,
      isUnInvitedSecondaryUser,
    );
    if (!sectionsProgressUpdate[sectionParentName]) {
      sectionsProgressUpdate[sectionParentName] = {
        name: sectionParentName,
        progress: 1,
        isComplete: false,
      };
    }
  }

  if (isSectionsProgressLoggingEnabled) {
    console.log('Initial Steps:', initialSteps);
    console.log('Section Progress Update:', sectionsProgressUpdate);
    console.groupEnd();
    console.log('');
  }

  return {
    updatedSteps: sectionsProgressUpdate[sectionName].steps,
    updatedSectionsProgress: sectionsProgressUpdate,
  };
};

export const getCompleteStep = ({
  currentSteps,
  isSectionsProgressLoggingEnabled,
  stepName,
  updatedSteps,
}: {
  currentSteps: SectionSteps,
  isSectionsProgressLoggingEnabled: boolean,
  stepName: SectionNames | Array<SectionNames>,
  updatedSteps: SectionSteps,
}): {
  update: SectionCompletionStatus,
  stepsUpdate: SectionSteps,
} => {
  const stepNamesToComplete = Array.isArray(stepName) ? stepName : [stepName];

  let stepsUpdate = updatedSteps ?? currentSteps;

  if (isSectionsProgressLoggingEnabled) {
    console.group('COMPLETE STEP');
    console.log({
      currentSteps,
      stepName,
      updatedSteps,
    });
  }

  if (!Boolean(stepsUpdate?.length)) {
    if (isSectionsProgressLoggingEnabled) {
      console.log('No steps to complete');
      console.groupEnd();
      console.log('');
    }

    return {
      update: null,
      stepsUpdate: null,
    };
  }

  for (const currentStepName of stepNamesToComplete) {
    stepsUpdate = stepsUpdate.map((step) =>
      step.name === currentStepName ? { ...step, complete: true } : step,
    );
  }

  const { required, completed } = getRequiredSteps({
    currentSectionProgressSteps: stepsUpdate,
    isSectionsProgressLoggingEnabled,
  });

  const progress = completionPercentage(completed.length, required.length);

  const update: SectionCompletionStatus = {
    progress: isNaN(progress) ? 0 : progress,
    steps: stepsUpdate,
  };

  if (isSectionsProgressLoggingEnabled) {
    console.log('Complete Step(s):', {
      stepName: stepNamesToComplete,
      stepsUpdate,
      update,
    });
    console.groupEnd();
    console.log('');
  }

  return {
    update,
    stepsUpdate,
  };
};

export const getIncompleteStep = ({
  currentSectionProgress,
  isSectionsProgressLoggingEnabled,
  requiredSteps,
  stepName,
}: {
  currentSectionProgress: SectionCompletionStatus,
  isSectionsProgressLoggingEnabled: boolean,
  requiredSteps: {
    completed: SectionSteps,
    required: SectionSteps,
  },
  stepName: SectionNames,
}): SectionCompletionStatus => {
  // Exclude the step marked as incomplete
  const updatedCompletedSteps = requiredSteps.completed.filter(
    (step) => step.name !== stepName,
  );

  const isFirstStep = stepName === currentSectionProgress.steps[0]?.name;

  const progress = completionPercentage(
    updatedCompletedSteps.length,
    requiredSteps.required.length,
  );

  const update: SectionCompletionStatus = {
    progress: isNaN(progress)
      ? 0
      : isFirstStep && progress === 0
        ? 1
        : progress,
    steps: currentSectionProgress.steps.map((step) =>
      step.name === stepName ? { ...step, complete: false } : step,
    ),
  };

  if (isSectionsProgressLoggingEnabled) {
    console.log('In-Complete Step:', {
      stepName,
      updatedCompletedSteps,
      update,
    });
  }

  return update;
};
